import type { BoxProps } from '@hover/blueprint';
import { Box, motion, useTheme } from '@hover/blueprint';
import { Grid, GridItem } from '@hover/blueprint/chakra';
import React, { useEffect, useRef, useState } from 'react';

import { NotFound } from 'src/modules/notFound/components/NotFound';
import { RenderingView } from 'src/modules/renderings/components/RenderingsHub/RenderingView';
import {
  RenderingVersions,
  RenderingsAccordion,
} from 'src/modules/renderings/components/RenderingsHub/RenderingsAccordion';
import { RenderingsList } from 'src/modules/renderings/components/RenderingsHub/RenderingsList';
import { calcContentNavigationGridTemplate } from 'src/modules/renderings/components/RenderingsHub/responsiveHelpers';
import { ReShareModal } from 'src/modules/renderings/components/ShareModal';
import { ShareButton } from 'src/modules/renderings/components/common/ShareButton';
import {
  concatAllImages,
  isRenderingArray,
} from 'src/modules/renderings/components/common/dataSanitation';
import { useScrollShadowPosition } from 'src/modules/renderings/components/common/hooks/useScrollShadowPosition';
import { useRenderingContext } from 'src/modules/renderings/hooks';
import { useFocusedView } from 'src/modules/renderings/hooks/useFocusedView';
import { useHeaderHeight } from 'src/modules/renderings/hooks/useHeaderHeight';
import { useIsDesktopSize } from 'src/modules/renderings/hooks/useIsDesktopSize';
import { useIsMobileSize } from 'src/modules/renderings/hooks/useIsMobileSize';
import { useRenderingSelection } from 'src/modules/renderings/hooks/useRenderingSelection/useRenderingSelection';
import type { ImageLike } from 'src/modules/renderings/imageLike';
import { getFirstRendering } from 'src/modules/renderings/utils/jobHelpers';
import { SkipSSR } from 'src/shared/components/SSR/SkipSSR';
import { FeatureTracking } from 'src/shared/constants/FeatureFlag';
import { useQueryParam, useReactNativeWebView } from 'src/shared/hooks';
import { Page, buttonPressed } from 'src/shared/telemetry';

import { Meta } from './Meta';

const RenderingsShared = () => {
  const { jobId, job, orgId, renderings, error } = useRenderingContext();
  const [isWebView] = useReactNativeWebView();
  const scrollContainer = useRef<HTMLDivElement>(null);
  const [heroImgIndex, setHeroImgIndex] = useState<number>(0);
  const { setRenderingFile, setRendering } = useRenderingSelection();
  let firstImageIndex = 0;
  const allImages = concatAllImages((renderings ?? [])[0]);
  const headerHeight = useHeaderHeight();

  const { containerShadowGradient, handleOnScroll } =
    useScrollShadowPosition(scrollContainer);

  const selectedImageId = useQueryParam('selectedImageId');
  if (isRenderingArray(renderings) && selectedImageId) {
    firstImageIndex = Math.max(
      allImages.findIndex(img => img.attachmentId === selectedImageId),
      0,
    );
  }

  const handleSetHeroImgUrl = (image: ImageLike) => {
    const { blobUrl: imageUrl } = image;
    const buttonPressedTrackingData = {
      button_text: 'CarouselSwipe',
      feature: FeatureTracking.RENDERING,
      job_id: jobId,
      org_id: orgId,
      page_or_screen_name: Page.RENDERINGS_HUB,
      primary_cta: false,
    };

    buttonPressed(buttonPressedTrackingData);

    setHeroImgIndex(
      Math.max(
        allImages.findIndex(({ blobUrl }) => blobUrl === imageUrl),
        0,
      ),
    );

    const selectedRendering = getFirstRendering(job);
    const selectedRenderingFile = selectedRendering?.versions
      .flatMap(version => version.hiDefRenderingFiles)
      .find(renderingFile => renderingFile.image.blobUrl === imageUrl);

    setRendering(selectedRendering);
    setRenderingFile(selectedRenderingFile);
  };

  useEffect(() => {
    setHeroImgIndex(firstImageIndex);
    const imageUrl = allImages[firstImageIndex]?.blobUrl;
    const selectedRendering = getFirstRendering(job);
    const selectedRenderingFile = selectedRendering?.versions
      .flatMap(version => version.hiDefRenderingFiles)
      .find(renderingFile => renderingFile.image.blobUrl === imageUrl);

    setRendering(selectedRendering);
    setRenderingFile(selectedRenderingFile);
    // This effect sets initial state for heroImgIndex, rendering, and renderingFile
    // It should be run again when firstImageIndex or job changes. All other
    // dependencies are ignored
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [firstImageIndex, job]);

  const heroImageURL = allImages[heroImgIndex]?.blobUrl;
  const { isFocusedView } = useFocusedView();
  const isMobileSize = useIsMobileSize();
  const isDesktopSize = useIsDesktopSize();
  const { colors } = useTheme();

  const gridTemplate = calcContentNavigationGridTemplate(
    isFocusedView,
    isDesktopSize,
    headerHeight,
  );

  const renderingListDisplay: BoxProps['display'] = isFocusedView
    ? 'none'
    : undefined;

  const backgroundColor = isFocusedView ? colors.neutral900 : colors.neutral0;
  const alignItems = isFocusedView ? 'center' : undefined;

  return (
    <>
      <Meta thumbnail={allImages[heroImgIndex]?.thumbUrl || ''} />
      <SkipSSR>
        {error ? (
          <NotFound />
        ) : (
          <Grid
            alignItems={alignItems}
            animate={{
              backgroundColor,
            }}
            as={motion.div}
            data-testid="shared-renderings"
            id="shared-renderings"
            layout
            {...gridTemplate}
          >
            <GridItem area="content" as={motion.div} layout>
              <RenderingView
                heroImgIndex={heroImgIndex}
                hideBackButton={!isWebView}
                images={allImages}
                onCarouselSwipe={setHeroImgIndex}
                shareButton={
                  <ShareButton
                    ShareModal={ReShareModal}
                    color={isFocusedView ? 'neutral50' : undefined}
                    iconOnly={isFocusedView && isMobileSize}
                  />
                }
              />
            </GridItem>
            <GridItem
              area="navigation"
              as={motion.div}
              display={renderingListDisplay}
              layout
              marginRight={isMobileSize ? 0 : 400}
              onScroll={handleOnScroll}
              overflowY={isDesktopSize ? 'scroll' : 'auto'}
            >
              <Box
                flexDirection="column"
                height="100%"
                ref={scrollContainer}
                width="100%"
              >
                <RenderingsList headerShadow={containerShadowGradient}>
                  {renderings.length > 0 && (
                    <RenderingsAccordion testId="renderings-accordion">
                      <RenderingVersions
                        handleImageSelect={handleSetHeroImgUrl}
                        heroImageURL={heroImageURL}
                        renderings={renderings}
                      />
                    </RenderingsAccordion>
                  )}
                </RenderingsList>
              </Box>
            </GridItem>
          </Grid>
        )}
      </SkipSSR>
    </>
  );
};
export { RenderingsShared };
